/**
 * Strict 5 Unit Global Enforcement System (Frontend)
 * 
 * This module provides core unit configuration and utility functions for the 
 * QRinv inventory management frontend. It enforces a strict 5-unit system:
 * - WEIGHT: g (base), kg (high)
 * - VOLUME: ml (base), L (high)
 * - COUNT: pcs (base and high)
 * 
 * This module mirrors the backend strictUnits.js implementation to ensure
 * consistent behavior across the entire application.
 * 
 * Database Unit IDs:
 * - 1 = kg (kilogram)
 * - 2 = g (gram)
 * - 4 = L (liter)
 * - 5 = ml (milliliter)
 * - 9 = pcs (piece)
 */

/**
 * Core unit configuration defining the three unit families and their properties.
 * Each family has a base unit for storage, a high unit for display, and a threshold
 * for auto-scaling between them.
 * 
 * @constant {Object} STRICT_UNITS
 * @property {Object} WEIGHT - Weight measurement family
 * @property {string} WEIGHT.base - Base unit symbol (g)
 * @property {number} WEIGHT.baseId - Database ID for base unit (2)
 * @property {string} WEIGHT.high - High unit symbol (kg)
 * @property {number} WEIGHT.highId - Database ID for high unit (1)
 * @property {number} WEIGHT.threshold - Conversion threshold (1000)
 * @property {Object} VOLUME - Volume measurement family
 * @property {string} VOLUME.base - Base unit symbol (ml)
 * @property {number} VOLUME.baseId - Database ID for base unit (7)
 * @property {string} VOLUME.high - High unit symbol (L)
 * @property {number} VOLUME.highId - Database ID for high unit (6)
 * @property {number} VOLUME.threshold - Conversion threshold (1000)
 * @property {Object} COUNT - Count measurement family
 * @property {string} COUNT.base - Base unit symbol (pcs)
 * @property {number} COUNT.baseId - Database ID for base unit (14)
 * @property {string} COUNT.high - High unit symbol (pcs)
 * @property {number} COUNT.highId - Database ID for high unit (14)
 * @property {number} COUNT.threshold - Conversion threshold (Infinity - never converts)
 */
export const STRICT_UNITS = {
  WEIGHT: { base: 'g', baseId: 2, high: 'kg', highId: 1, threshold: 1000 },
  VOLUME: { base: 'ml', baseId: 5, high: 'L', highId: 4, threshold: 1000 },
  COUNT: { base: 'pcs', baseId: 9, high: 'pcs', highId: 9, threshold: Infinity }
};

/**
 * Mapping of unit symbols to their family names.
 * Used to determine which family a given unit belongs to.
 * 
 * @constant {Object.<string, string>} UNIT_FAMILIES
 */
export const UNIT_FAMILIES = {
  // mg is not offered as a choice (Strict-5 is kg/g/L/ml/pcs) but items ARE
  // stored in it, and the backend has always called it mass. Without it here an
  // mg item has NO family, so the dropdown offers all five units including
  // cross-family ones.
  'mg': 'WEIGHT',
  'g': 'WEIGHT',
  'kg': 'WEIGHT',
  'ml': 'VOLUME',
  'L': 'VOLUME',
  'pcs': 'COUNT'
};

/**
 * Mapping of database unit IDs to their unit symbols.
 * 
 * @constant {Object.<number, string>} UNIT_ID_MAP
 */
export const UNIT_ID_MAP = {
  1: 'kg',
  2: 'g',
  4: 'L',
  5: 'ml',
  9: 'pcs'
};

/**
 * Applies precision guard to a numeric value.
 * Rounds to 4 decimal places and snaps to 0 if value is below threshold.
 * 
 * @param {number} value - The numeric value to guard
 * @returns {number} The guarded value (rounded to 4 decimals, snapped to 0 if < 0.0001)
 * 
 * @example
 * applyPrecisionGuard(1.23456789) // returns 1.2346
 * applyPrecisionGuard(0.00005) // returns 0
 * applyPrecisionGuard(0.0005) // returns 0.0005
 */
export function applyPrecisionGuard(value) {
  // Zero-snap: if absolute value is less than 0.0001, return 0
  // This must be checked BEFORE rounding to catch values that would round up to 0.0001
  if (Math.abs(value) < 0.0001) {
    return 0;
  }
  
  // Round to 4 decimal places
  const rounded = Math.round(value * 10000) / 10000;
  
  return rounded;
}

/**
 * Gets the family name for a given unit symbol.
 * 
 * @param {string} unit - The unit symbol (g, kg, ml, L, pcs)
 * @returns {string|null} The family name (WEIGHT, VOLUME, COUNT) or null if unit is invalid
 * 
 * @example
 * getUnitFamily('kg') // returns 'WEIGHT'
 * getUnitFamily('ml') // returns 'VOLUME'
 * getUnitFamily('pcs') // returns 'COUNT'
 * getUnitFamily('invalid') // returns null
 */
export function getUnitFamily(unit) {
  // Canonicalised first. This was an exact-string lookup, so every alias the
  // data actually holds — 'gm', 'kgs', 'ltr', 'mg' — returned null. A null
  // family makes SmartUnitDropdown offer all five units instead of the two in
  // that family, and the row handlers then relabel the number: an item stored
  // as 'gm' could be switched to 'L' in two clicks, with no conversion.
  return UNIT_FAMILIES[unit] || UNIT_FAMILIES[toCanonicalUnit(unit)] || null;
}

/**
 * Checks if changing from one unit to another is a cross-family change.
 * A cross-family change occurs when the two units belong to different families.
 * 
 * @param {string} fromUnit - The current unit symbol
 * @param {string} toUnit - The target unit symbol
 * @returns {boolean} True if the units are from different families, false otherwise
 * 
 * @example
 * isCrossFamilyChange('g', 'kg') // returns false (same family)
 * isCrossFamilyChange('g', 'ml') // returns true (different families)
 * isCrossFamilyChange('pcs', 'L') // returns true (different families)
 */
export function isCrossFamilyChange(fromUnit, toUnit) {
  const fromFamily = getUnitFamily(fromUnit);
  const toFamily = getUnitFamily(toUnit);
  
  // If either unit is invalid, consider it a cross-family change
  if (!fromFamily || !toFamily) {
    return true;
  }
  
  return fromFamily !== toFamily;
}

/**
 * Normalizes a quantity to its base unit.
 * Converts high units (kg, L) to their base equivalents (g, ml).
 * Base units and pcs remain unchanged.
 * 
 * @param {number} quantity - The quantity value to normalize
 * @param {string} unit - The unit symbol of the quantity
 * @returns {{value: number, baseUnit: string}} Object containing the normalized value and base unit
 * 
 * @example
 * normalizeToBase(1.5, 'kg') // returns { value: 1500, baseUnit: 'g' }
 * normalizeToBase(2, 'L') // returns { value: 2000, baseUnit: 'ml' }
 * normalizeToBase(500, 'g') // returns { value: 500, baseUnit: 'g' }
 * normalizeToBase(10, 'pcs') // returns { value: 10, baseUnit: 'pcs' }
 */
export function normalizeToBase(quantity, unit) {
  const family = getUnitFamily(unit);
  
  if (!family) {
    throw new Error(`Invalid unit: ${unit}`);
  }
  
  const familyConfig = STRICT_UNITS[family];
  const baseUnit = familyConfig.base;
  
  // If already in base unit, return as-is
  if (unit === baseUnit) {
    return { value: applyPrecisionGuard(quantity), baseUnit };
  }
  
  // If in high unit, convert to base by multiplying by threshold
  if (unit === familyConfig.high) {
    const normalizedValue = quantity * familyConfig.threshold;
    return { value: applyPrecisionGuard(normalizedValue), baseUnit };
  }
  
  // Should not reach here if unit validation is correct
  throw new Error(`Unit ${unit} is not valid for family ${family}`);
}

/**
 * Force a quantity into its family's base unit — kg -> g, L -> ml — passing through
 * anything that has no base form.
 *
 * A tolerant wrapper around normalizeToBase for callers that must not throw. Recipe rows
 * can legitimately carry a kitchen unit (tbsp, ladle, cup) that belongs to no strict
 * family; those keep their unit and value untouched rather than being converted with an
 * invented factor or blowing up the whole batch they arrived in.
 *
 * This is deliberately ONE-WAY. formatUnitDisplay / autoScaleToSymbol scale in both
 * directions — they pull 0.07 kg down to 70 g, but they also push 1200 g up to 1.2 kg —
 * which is right for display and wrong for storage: it makes the stored unit depend on how
 * large the number happens to be.
 *
 * @param {number} quantity
 * @param {string} unit
 * @returns {{value: number, unit: string}}
 *
 * @example
 * toBaseUnitOrKeep(1.5, 'kg')   // { value: 1500, unit: 'g' }
 * toBaseUnitOrKeep(2, 'L')      // { value: 2000, unit: 'ml' }
 * toBaseUnitOrKeep(1200, 'g')   // { value: 1200, unit: 'g' }   <- NOT 1.2 kg
 * toBaseUnitOrKeep(3, 'tbsp')   // { value: 3, unit: 'tbsp' }
 */
/**
 * THE frontend converter. Same-family conversion through the base unit; cross-family
 * or unknown units return null — never a 1:1 density guess (the screens' old local
 * convertQuantity copies added g and ml as if they were the same thing).
 *
 * @param {number} quantity
 * @param {string} fromUnit  any symbol/alias
 * @param {string} toUnit    any symbol/alias
 * @returns {number|null}
 */
export function convert(quantity, fromUnit, toUnit) {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return null;

  const from = toCanonicalUnit(fromUnit);
  const to = toCanonicalUnit(toUnit);
  if (!from || !to) return null;
  if (from === to) return value;

  const fromFamily = getUnitFamily(from);
  const toFamily = getUnitFamily(to);
  if (!fromFamily || fromFamily !== toFamily) return null;

  const { value: baseValue } = normalizeToBase(value, from);
  const toConfig = STRICT_UNITS[toFamily];
  return to === toConfig.high ? baseValue / toConfig.threshold : baseValue;
}

/**
 * SP9 "your number is sacred": the metric sibling of a value, for the tap-to-apply
 * equivalence hint. 2 kg -> { value: 2000, unit: 'g' }; 1500 g -> { value: 1.5, unit: 'kg' }.
 * Conversion is something the user ASKS for — this only computes, never rewrites state.
 *
 * @returns {{ value: number, unit: string } | null} null for non-metric units, zero, or
 *   an equivalence not worth showing (identical value).
 */
export function equivalentIn(quantity, unit) {
  const value = Number(quantity);
  if (!Number.isFinite(value) || value <= 0) return null;
  const canonical = toCanonicalUnit(unit);
  const familyName = UNIT_FAMILIES[canonical];
  if (!familyName || familyName === 'COUNT') return null;
  const config = STRICT_UNITS[familyName];
  const sibling = canonical === config.base ? config.high : config.base;
  const converted = convert(value, canonical, sibling);
  if (converted === null || !Number.isFinite(converted)) return null;
  const rounded = Number(converted.toFixed(4));
  if (rounded <= 0) return null;
  return { value: rounded, unit: sibling };
}

export function toBaseUnitOrKeep(quantity, unit) {
  const value = Number(quantity);
  const safeValue = Number.isFinite(value) ? value : 0;
  const canonical = toCanonicalUnit(unit);

  if (!getUnitFamily(canonical)) {
    return { value: safeValue, unit: unit || canonical };
  }

  const { value: baseValue, baseUnit } = normalizeToBase(safeValue, canonical);
  return { value: baseValue, unit: baseUnit };
}

/**
 * Formats a quantity for display with auto-scaling logic.
 * Automatically scales between base and high units based on magnitude.
 * - Quantities >= threshold are displayed in high units
 * - Quantities < threshold are displayed in base units
 * - COUNT family always displays in pcs regardless of magnitude
 * 
 * @param {number} rawQuantity - The quantity in base units
 * @param {string} rawUnit - The base unit symbol (g, ml, pcs)
 * @returns {{qty: number, unit: string}} Object containing the formatted quantity and display unit
 * 
 * Scaling keys off the MAGNITUDE, so negative stock scales the same way as
 * positive: |value| < 1000 shows the base unit (g/ml), |value| >= 1000 shows
 * the high unit (kg/L). Without the abs(), a value like -163385 is always
 * "< 1000" and would wrongly render as "-1,63,385 g" instead of "-163.385 kg".
 *
 * @example
 * formatUnitDisplay(2500, 'g')   // returns { qty: 2.5, unit: 'kg' }
 * formatUnitDisplay(500, 'g')    // returns { qty: 500, unit: 'g' }
 * formatUnitDisplay(1500, 'ml')  // returns { qty: 1.5, unit: 'L' }
 * formatUnitDisplay(-500, 'g')   // returns { qty: -500, unit: 'g' }
 * formatUnitDisplay(-163385, 'g')// returns { qty: -163.385, unit: 'kg' }
 * formatUnitDisplay(100, 'pcs')  // returns { qty: 100, unit: 'pcs' }
 */
export function formatUnitDisplay(rawQuantity, rawUnit) {
  const family = getUnitFamily(rawUnit);

  if (!family) {
    throw new Error(`Invalid unit: ${rawUnit}`);
  }

  const familyConfig = STRICT_UNITS[family];

  // Always use base unit for COUNT family
  if (family === 'COUNT') {
    return { qty: applyPrecisionGuard(rawQuantity), unit: rawUnit };
  }

  // First normalize to base unit if needed
  const { value: baseValue, baseUnit } = normalizeToBase(rawQuantity, rawUnit);

  // Below threshold in MAGNITUDE → base unit (handles negatives symmetrically).
  if (Math.abs(baseValue) < familyConfig.threshold) {
    return { qty: applyPrecisionGuard(baseValue), unit: baseUnit };
  }

  // At or above threshold in magnitude → high unit (sign preserved by division).
  const highValue = baseValue / familyConfig.threshold;
  return { qty: applyPrecisionGuard(highValue), unit: familyConfig.high };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CANONICAL DISPLAY LABELS (single source of truth for the whole UI)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Title-case, British full-name labels for the 5 canonical units.
 * Every on-screen quantity+unit must render through formatQuantity() so the whole
 * app uses this exact vocabulary (auto-scaled at 1000).
 */
export const UNIT_DISPLAY_LABELS = {
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  L: 'l',
  pcs: 'pcs'
};

/**
 * Normalize any unit string (symbol, alias, or full name — "g", "gram", "Gram",
 * "kilograms", "ltr", "Milliliter", "L", "pieces"…) to a canonical short symbol
 * (g/kg/ml/L/pcs). Returns null for unrecognized units.
 *
 * @param {string} rawUnit
 * @returns {('g'|'kg'|'ml'|'L'|'pcs'|null)}
 */
export function toCanonicalUnit(rawUnit) {
  const raw = String(rawUnit || '').trim();
  if (!raw) return null;
  if (raw === 'L') return 'L'; // case-sensitive litre symbol
  const u = raw.toLowerCase();
  if (['g', 'gm', 'gms', 'gram', 'grams'].includes(u)) return 'g';
  if (['kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms'].includes(u)) return 'kg';
  if (['ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'].includes(u)) return 'ml';
  if (['l', 'lt', 'ltr', 'liter', 'litre', 'liters', 'litres'].includes(u)) return 'L';
  if (['pcs', 'pc', 'piece', 'pieces', 'unit', 'units', 'nos', 'no', 'number', 'numbers'].includes(u)) return 'pcs';
  return null;
}

/**
 * Format a number for display: round to 4dp and trim trailing zeros.
 * 250.0000 -> "250", 2.5000 -> "2.5", 0.0187 -> "0.0187"
 *
 * @param {number|string} value
 * @returns {string}
 */
// Indian digit grouping, done by hand: last 3, then pairs. 2000 -> "2,000".
export function groupIndian(intStr) {
  const s = String(intStr);
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${rest},${last3}`;
}

/**
 * Format a number with a DOT decimal separator regardless of device locale.
 *
 * toLocaleString('en-IN', …) silently falls back to the host locale when that
 * locale's data is unavailable — common in Android WebViews — which renders
 * 0.1 as "0,1". On a stock sheet that is indistinguishable from zero, and
 * people have read it as zero. Formatting by hand removes the ambiguity.
 */
export function formatFixedDot(value, maxFrac) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value ?? '');
  const neg = num < 0;
  // Strip trailing zeros ONLY when there is a decimal point to strip them from.
  // Unanchored, /\.?0+$/ ate the zeros of whole numbers: toFixed(0) on 100 is
  // "100", which came back as "1".
  const raw = Math.abs(num).toFixed(maxFrac);
  const fixed = raw.includes('.') ? raw.replace(/\.?0+$/, '') : raw;
  const [i, f] = fixed.split('.');
  return `${neg ? '-' : ''}${groupIndian(i || '0')}${f ? '.' + f : ''}`;
}

export function formatQuantityNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value ?? '');
  // Indian grouping + trimmed decimals: 2000 -> "2,000", 2.5 -> "2.5", 0.0187 -> "0.0187"
  return formatFixedDot(parseFloat(num.toFixed(4)), 4);
}

/**
 * Auto-scale a quantity to its readable SHORT symbol (g/kg/ml/L/pcs) — same 1000-threshold
 * rule as formatQuantity, but returns the numeric qty + short unit symbol for EDITABLE
 * contexts (quantity input + unit dropdown), e.g. the preparation & complimentary editors.
 * Unknown units pass through unchanged.
 *
 * @param {number|string} quantity
 * @param {string} rawUnit
 * @returns {{qty: number, unit: string}}
 *
 * @example
 * autoScaleToSymbol(0.01, 'kg') // { qty: 10,   unit: 'g' }
 * autoScaleToSymbol(1, 'kg')    // { qty: 1,    unit: 'kg' }
 * autoScaleToSymbol(2500, 'g')  // { qty: 2.5,  unit: 'kg' }
 */
export function autoScaleToSymbol(quantity, rawUnit) {
  const qtyNum = Number(quantity);
  const canonical = toCanonicalUnit(rawUnit);
  if (!canonical || !Number.isFinite(qtyNum)) {
    return { qty: Number.isFinite(qtyNum) ? qtyNum : 0, unit: String(rawUnit || '').trim() };
  }
  const { value, baseUnit } = normalizeToBase(qtyNum, canonical);
  const scaled = formatUnitDisplay(value, baseUnit); // { qty:number, unit:'g'|'kg'|'ml'|'L'|'pcs' }
  return { qty: scaled.qty, unit: scaled.unit };
}

/**
 * CANONICAL display formatter — the ONE function every screen should use.
 * Auto-scales at 1000 and returns Title-case British full-name labels.
 *
 * @param {number|string} rawQuantity
 * @param {string} rawUnit  - any symbol / alias / full name
 * @returns {{qty: string, unit: string, label: string}}
 *
 * @example
 * formatQuantityParts(0.2, 'kg')  // { qty: '200',  unit: 'Gram',       label: '200 Gram' }
 * formatQuantityParts(2500, 'g')  // { qty: '2.5',  unit: 'Kilogram',   label: '2.5 Kilogram' }
 * formatQuantityParts(0.1, 'L')   // { qty: '100',  unit: 'Millilitre', label: '100 Millilitre' }
 * formatQuantityParts(3, 'pcs')   // { qty: '3',    unit: 'Pcs',        label: '3 Pcs' }
 */
export function formatQuantityParts(rawQuantity, rawUnit) {
  const canonical = toCanonicalUnit(rawUnit);

  // Unknown unit: display the value verbatim with whatever unit string we got.
  if (!canonical) {
    const qty = formatQuantityNumber(rawQuantity);
    const unit = String(rawUnit || '').trim();
    return { qty, unit, label: unit ? `${qty} ${unit}` : qty };
  }

  const scaled = formatUnitDisplay(rawQuantity, canonical); // { qty:number, unit:'g'|'kg'|'ml'|'L'|'pcs' }
  const qty = formatQuantityNumber(scaled.qty);
  const unit = UNIT_DISPLAY_LABELS[scaled.unit] || scaled.unit;
  return { qty, unit, label: `${qty} ${unit}` };
}

/**
 * CANONICAL display string — "200 Gram", "2.5 Kilogram", "100 Millilitre", "3 Pcs".
 * @param {number|string} rawQuantity
 * @param {string} rawUnit
 * @returns {string}
 */
export function formatQuantity(rawQuantity, rawUnit) {
  return formatQuantityParts(rawQuantity, rawUnit).label;
}

/**
 * The BASE symbol of a unit's family — kg -> g, L -> ml, pcs -> pcs.
 *
 * For unit pills that name an ingredient's measure without a quantity attached. A pill
 * reading "kg" invites the reader to think in kilos and then type a kilo-sized number into
 * a field that is stored in grams; showing the base unit keeps the label and the storage
 * telling the same story. Purely a label — it converts no values.
 *
 * Unknown units (tbsp, cup) pass through unchanged rather than being forced into a family
 * they do not belong to.
 *
 * @param {string} rawUnit
 * @returns {string}
 *
 * @example
 * toBaseUnitSymbol('kg')    // 'g'
 * toBaseUnitSymbol('Litre') // 'ml'
 * toBaseUnitSymbol('pcs')   // 'pcs'
 * toBaseUnitSymbol('tbsp')  // 'tbsp'
 */
export function toBaseUnitSymbol(rawUnit) {
  const canonical = toCanonicalUnit(rawUnit);
  const family = getUnitFamily(canonical);
  if (!family) return String(rawUnit || '').trim();
  return STRICT_UNITS[family].base;
}

/**
 * CANONICAL bare unit label (no quantity) — for unit pills/columns.
 * "kg" -> "Kilogram", "ml" -> "Millilitre", "Gram" -> "Gram". Unknown units pass through.
 * @param {string} rawUnit
 * @returns {string}
 */
export function formatUnitLabel(rawUnit) {
  const canonical = toCanonicalUnit(rawUnit);
  if (!canonical) return String(rawUnit || '').trim();
  return UNIT_DISPLAY_LABELS[canonical] || canonical;
}

/**
 * Default export for convenience (allows both named and default imports)
 */
export default {
  STRICT_UNITS,
  UNIT_FAMILIES,
  UNIT_ID_MAP,
  UNIT_DISPLAY_LABELS,
  formatUnitDisplay,
  formatQuantity,
  formatQuantityParts,
  formatQuantityNumber,
  formatUnitLabel,
  toBaseUnitSymbol,
  autoScaleToSymbol,
  toCanonicalUnit,
  normalizeToBase,
  toBaseUnitOrKeep,
  getUnitFamily,
  isCrossFamilyChange,
  applyPrecisionGuard
};
